import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTRY } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, X, BookOpen, Pencil, FileSpreadsheet, FileDown } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTurkishFont } from "@/lib/pdfFont";
import { TR_BANKS, BANK_DATALIST_ID } from "@/lib/banks";

export default function ChequeBooks() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const highlightRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cheque-books");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ?highlight=<book_id> ile gelindiyse ilgili satıra scroll et + 3sn sarı vurgu.
  useEffect(() => {
    if (!highlightId || loading || !items.length) return;
    const el = highlightRefs.current[highlightId];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => {
      // vurgu bittikten sonra query paramı temizle
      setSearchParams({}, { replace: true });
    }, 3000);
    return () => clearTimeout(t);
  }, [highlightId, loading, items, setSearchParams]);

  // Sekmeye/pencereye geri dönünce koçan sayaçlarını tazele — kullanıcı başka sekmede
  // çek eklediğinde bu sayfa açıksa güncel görünsün.
  useEffect(() => {
    const onFocus = () => load();
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  const remove = async (id, bank) => {
    if (!window.confirm(`${bank} koçanı silinsin mi?`)) return;
    try {
      await api.delete(`/cheque-books/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error("Silinemedi: " + (e?.response?.data?.detail || e?.message || ""));
    }
  };

  const totals = useMemo(() => {
    const t = items.reduce(
      (acc, b) => ({
        total: acc.total + (b.total_count || 0),
        used: acc.used + (b.used_count || 0),
        unused: acc.unused + (b.unused_count || 0),
        used_amount: acc.used_amount + (b.used_amount || 0),
      }),
      { total: 0, used: 0, unused: 0, used_amount: 0 },
    );
    return t;
  }, [items]);

  const exportExcel = () => {
    if (!items.length) { toast.error("Aktarılacak koçan yok"); return; }
    const rows = items.map((b) => ({
      "Banka": b.bank_name,
      "Koçan Başlangıç No": b.start_no,
      "Koçan Bitiş No": b.end_no,
      "Koçandaki Çek Adedi": b.total_count,
      "Kullanılmış": b.used_count,
      "Kullanılmamış": b.unused_count,
      "Kullanılmış Tutar (₺)": b.used_amount || 0,
      "Not": b.notes || "",
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 16 }, { wch: 20 }, { wch: 32 }];
    XLSX.utils.book_append_sheet(wb, ws, "Çek Koçanları");
    XLSX.writeFile(wb, `cek-kocanlari_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Excel indirildi");
  };

  const exportPDF = async () => {
    if (!items.length) { toast.error("Aktarılacak koçan yok"); return; }
    try {
      const { data: c } = await api.get("/settings/company");
      const doc = new jsPDF({ compress: true, orientation: "landscape" });
      await registerTurkishFont(doc);
      // Logo
      try {
        const res = await fetch("/plasper-logo.jpg");
        const blob = await res.blob();
        const dataUrl = await new Promise((r) => { const fr = new FileReader(); fr.onloadend = () => r(fr.result); fr.readAsDataURL(blob); });
        doc.addImage(dataUrl, "JPEG", 14, 10, 40, 13, undefined, "FAST");
      } catch {}
      doc.setFontSize(16); doc.setFont(undefined, "bold");
      doc.text("ÇEK KOÇAN DURUM RAPORU", 60, 18);
      doc.setFont(undefined, "normal"); doc.setFontSize(9); doc.setTextColor(90);
      doc.text(c?.name || "", 60, 24);
      doc.text(`Rapor Tarihi: ${new Date().toLocaleDateString("tr-TR")}`, 60, 29);
      doc.setTextColor(0);
      autoTable(doc, {
        startY: 38,
        head: [["Banka", "Başlangıç No", "Bitiş No", "Toplam", "Kullanılmış", "Kullanılmamış", "Kul. Tutar (₺)", "Not"]],
        body: items.map((b) => [
          b.bank_name, b.start_no, b.end_no, b.total_count,
          b.used_count, b.unused_count,
          (b.used_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          b.notes || "",
        ]),
        foot: [["TOPLAM", "", "", totals.total, totals.used, totals.unused,
          totals.used_amount.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), ""]],
        styles: { fontSize: 9, font: "Roboto", cellPadding: 2 },
        headStyles: { fillColor: [10, 10, 10], font: "Roboto", fontStyle: "bold" },
        footStyles: { fillColor: [230, 230, 230], font: "Roboto", fontStyle: "bold", textColor: 0 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { halign: "right", cellWidth: 26 },
          2: { halign: "right", cellWidth: 26 },
          3: { halign: "right", cellWidth: 22 },
          4: { halign: "right", cellWidth: 28, textColor: [220, 38, 38] },
          5: { halign: "right", cellWidth: 32, textColor: [5, 150, 105] },
          6: { halign: "right", cellWidth: 34 },
        },
      });
      doc.save(`cek-kocanlari_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error("PDF oluşturulamadı: " + (e?.message || ""));
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Bankaya Verilen Çek Koçanları</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Çek Koçanları</h1>
          <p className="text-xs text-neutral-500 mt-1.5 max-w-xl">
            Bankaya kestirdiğiniz koçan aralıklarını takip edin. Kullanılmış yaprak sayısı,
            aynı banka için verdiğiniz çeklerin numaralarına göre otomatik hesaplanır.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportExcel}
            data-testid="cheque-book-export-excel"
            className="brutal-btn bg-white text-black border border-black px-3 py-2.5 text-sm flex items-center gap-2"
            title="Excel olarak indir"
          >
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button
            onClick={exportPDF}
            data-testid="cheque-book-export-pdf"
            className="brutal-btn bg-white text-black border border-black px-3 py-2.5 text-sm flex items-center gap-2"
            title="PDF olarak indir"
          >
            <FileDown size={14} /> PDF
          </button>
          <button
            data-testid="cheque-book-add"
            onClick={() => { setEditing(null); setOpen(true); }}
            className="brutal-btn bg-black text-white px-4 py-2.5 text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} /> Yeni Koçan
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Toplam Yaprak" value={totals.total} tone="text-black" />
        <Stat label="Kullanılan" value={totals.used} tone="text-rose-600" />
        <Stat label="Kullanılmayan" value={totals.unused} tone="text-emerald-700" />
        <Stat label="Kullanılan Tutar" value={fmtTRY(totals.used_amount)} tone="text-black" isMoney />
      </div>

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="cheque-books-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="text-left px-4 py-3 label-caps">Banka</th>
              <th className="text-right px-4 py-3 label-caps">Koçan Başlangıç No</th>
              <th className="text-right px-4 py-3 label-caps">Koçan Bitiş No</th>
              <th className="text-right px-4 py-3 label-caps">Koçandaki Çek Adedi</th>
              <th className="text-right px-4 py-3 label-caps">Kullanılmış</th>
              <th className="text-right px-4 py-3 label-caps">Kullanılmamış</th>
              <th className="text-right px-4 py-3 label-caps">Kul. Tutar</th>
              <th className="text-left px-4 py-3 label-caps">Not</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-neutral-500">Yükleniyor…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-10 text-neutral-500 text-sm">
                  Henüz koçan yok. <b>Yeni Koçan</b> ile başlayın.
                </td>
              </tr>
            ) : items.map((b) => (
              <tr
                key={b.id}
                ref={(el) => { if (el) highlightRefs.current[b.id] = el; }}
                data-testid={`cheque-book-row-${b.id}`}
                className={`border-b border-black/5 transition-colors ${
                  highlightId === b.id
                    ? "bg-amber-100 ring-2 ring-amber-500"
                    : "hover:bg-neutral-50"
                }`}
              >
                <td className="px-4 py-3 font-medium">
                  <span className="inline-flex items-center gap-2">
                    <BookOpen size={14} className="text-neutral-400" />
                    {b.bank_name}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono-num">{b.start_no}</td>
                <td className="px-4 py-3 text-right font-mono-num">{b.end_no}</td>
                <td className="px-4 py-3 text-right font-mono-num font-semibold">{b.total_count}</td>
                <td className="px-4 py-3 text-right font-mono-num text-rose-600">{b.used_count}</td>
                <td className="px-4 py-3 text-right font-mono-num text-emerald-700 font-semibold">{b.unused_count}</td>
                <td className="px-4 py-3 text-right font-mono-num" data-testid={`cheque-book-used-amount-${b.id}`}>{fmtTRY(b.used_amount || 0)}</td>
                <td className="px-4 py-3 text-neutral-600 text-xs max-w-[240px] truncate" title={b.notes || ""}>{b.notes || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`cheque-book-edit-${b.id}`}
                    onClick={() => { setEditing(b); setOpen(true); }}
                    className="p-1 hover:bg-neutral-100 mr-1"
                    title="Düzenle"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={`cheque-book-delete-${b.id}`}
                    onClick={() => remove(b.id, b.bank_name)}
                    className="p-1 hover:bg-rose-50"
                    title="Sil"
                  >
                    <Trash2 size={14} className="text-rose-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <BookModal
          editing={editing}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone, isMoney }) {
  return (
    <div className="brutal-card p-5">
      <div className="label-caps">{label}</div>
      <div className={`font-mono-num ${isMoney ? "text-xl md:text-2xl" : "text-3xl"} mt-2 ${tone || ""}`}>{value}</div>
    </div>
  );
}

function BookModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || { bank_name: "", start_no: "", end_no: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const total = useMemo(() => {
    const s = parseInt(form.start_no, 10);
    const e = parseInt(form.end_no, 10);
    if (isNaN(s) || isNaN(e) || e < s) return null;
    return e - s + 1;
  }, [form.start_no, form.end_no]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.bank_name?.trim()) { toast.error("Banka adı zorunlu"); return; }
    const s = parseInt(form.start_no, 10);
    const en = parseInt(form.end_no, 10);
    if (isNaN(s) || s <= 0) { toast.error("Başlangıç numarası geçersiz"); return; }
    if (isNaN(en) || en <= 0) { toast.error("Bitiş numarası geçersiz"); return; }
    if (en < s) { toast.error("Bitiş numarası, başlangıçtan küçük olamaz"); return; }
    setSaving(true);
    try {
      const payload = {
        bank_name: form.bank_name.trim(),
        start_no: s, end_no: en,
        notes: (form.notes || "").trim() || null,
      };
      if (editing) await api.put(`/cheque-books/${editing.id}`, payload);
      else await api.post("/cheque-books", payload);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      onSaved();
    } catch (err) {
      toast.error("Kaydedilemedi: " + (err?.response?.data?.detail || err?.message || ""));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            data-testid="cheque-book-modal"
            className="bg-white border border-black w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Koçanı Düzenle" : "Yeni Koçan"}</h2>
          <button type="button" onClick={onClose} className="p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <F label="Banka">
            <input
              required
              list={BANK_DATALIST_ID}
              data-testid="cheque-book-bank"
              value={form.bank_name || ""}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
              className="input"
              placeholder="Listeden seçin veya yazın"
              autoComplete="off"
            />
            <datalist id={BANK_DATALIST_ID}>
              {TR_BANKS.map((b) => <option key={b} value={b} />)}
            </datalist>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Koçan Başlangıç No">
              <input
                required type="number" min="1"
                data-testid="cheque-book-start"
                value={form.start_no ?? ""}
                onChange={(e) => setForm({ ...form, start_no: e.target.value })}
                className="input font-mono-num"
              />
            </F>
            <F label="Koçan Bitiş No">
              <input
                required type="number" min="1"
                data-testid="cheque-book-end"
                value={form.end_no ?? ""}
                onChange={(e) => setForm({ ...form, end_no: e.target.value })}
                className="input font-mono-num"
              />
            </F>
          </div>
          {total !== null && (
            <div className="text-xs text-neutral-600 border-l-2 border-black pl-2">
              Bu koçanda <b className="font-mono-num">{total}</b> yaprak var.
            </div>
          )}
          <F label="Not (opsiyonel)">
            <textarea
              rows={2}
              data-testid="cheque-book-notes"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input"
              placeholder="Koçan hakkında ek bilgi"
            />
          </F>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button
            data-testid="cheque-book-save"
            type="submit"
            disabled={saving}
            className="brutal-btn bg-black text-white px-5 py-2 text-sm font-medium disabled:opacity-40"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.55rem 0.75rem;outline:none;font-size:0.9rem}
                  .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1.5">{label}</div>
      {children}
    </label>
  );
}
